'use es6';

import SeamlessImmutable from 'seamless-immutable';
import { object } from 'underscore'; //
// TODO: Some of this can be deleted when we move to Lodash.
//

/**
 * @type {Object}
 * @readonly
 */

export const EMPTY_OBJECT = SeamlessImmutable({});
/**
 * Returns true if the passed param is an object literal, or created using `new` or `Object.create()`.
 * Allows for null or voided prototypes.
 *
 * `_.isObject` works correctly, but remember that things like functions are also objects.
 *
 * @param {Any} obj
 * @return {Boolean}
 */

export function isPlainObject(obj) {
  return typeof obj === 'object' && obj === Object(obj);
}
/**
 * Creates a clone of an object using JSON methods.
 *
 * Note this is not a true deep clone, since objects can strip fields
 * via the `toJSON()` interface, and since fields whose property descriptor
 * is `enumerable: false` will not be picked up.
 *
 * TODO: If anyone needs a deep clone method before we switch to Lodash, we can
 *       write one easily enough. See models/ReadOnly.js for an example prototype of recursion.
 *
 * @param {Any} obj
 * @return {Object|Any}
 */

export function jsonClone(obj) {
  if (obj == null) {
    return obj;
  }

  if (!isPlainObject) {
    return {};
  }

  return JSON.parse(JSON.stringify(obj));
}
/**
 * Calls `Object.freeze()` recursively down an object and its property trees.
 *
 * @param {Object}  obj                   Object to freeze.
 * @param {Boolean} includeNonEnumerable  True to include non-enumerable properties; default is false.
 * @return {Object} The original object passed, now deeply frozen.
 */

export function deepFreeze(obj, includeNonEnumerable = false) {
  if (obj !== Object(obj)) {
    return obj;
  }

  if (!Object.isFrozen(obj)) {
    Object.freeze(obj);
  }

  (includeNonEnumerable ? Object.getOwnPropertyDescriptor : Object.keys)(obj).forEach(key => deepFreeze(obj[key], includeNonEnumerable));
  return obj;
}
/**
 * Creates an optimized negation function (omit/pick)
 * that uses an indexed dict. Over 6x faster than underscore.
 *
 * @param  {Array}    list      Array of field names.
 * @param  {Boolean}  include   True => Pick, False => Omit
 * @param  {Boolean}  useCache  True to cache results on object passed to result function.
 * @return {Function} An optimized omit/pick function.
 */

function createObjectNegationFunction(list, include, useCache) {
  const dict = object(list, list);
  const cache = new WeakMap();
  return function negation(obj) {
    if (useCache && cache.has(obj)) {
      return cache.get(obj);
    }

    const keys = Object.keys(obj);
    const newObj = {};

    for (let i = keys.length; i--;) {
      const key = keys[i];
      const isKeyInDict = (key in dict);

      if (include && isKeyInDict || !include && !isKeyInDict) {
        newObj[key] = obj[key];
      }
    }

    if (useCache) {
      cache.set(obj, newObj);
    }

    return newObj;
  };
}
/**
 * Creates an optimized omit function.
 *
 * @param  {Array} blacklist
 * @param  {Boolean} useCache Optionally cache result of omit on passed object. Default is false.
 * @return {Function} omit(obj) => {}
 */


export const createOptimizedOmit = (blacklist, useCache = false) => createObjectNegationFunction(blacklist, false, useCache);
/**
 * Creates an optimized pick function.
 *
 * @param  {Array} whitelist
 * @param  {Boolean} useCache Optionally cache result of pick on passed object. Default is false.
 * @return {Function} pick(obj) => {}
 */

export const createOptimizedPick = (whitelist, useCache = false) => createObjectNegationFunction(whitelist, true, useCache);
/**
 * Similar to `_.defaults`, however `defaultData` represents all possible
 * fields.  Only those fields in `data` that are also in `defaultData` will
 * be copied into the new object.  If there are none in `data`, then
 * `defaultData` will be returned.
 *
 * @param  {Object} defaultData   Master version of defaults.
 * @param  {Object} data          Data to override fields in `defaultData`.
 * @return {Object}               A new defaults object if there were overrides, otherwise `defaultData`.
 */

export function defaults(defaultData, data) {
  let hasChanges = false;
  let newDefaults;
  const defaultKeys = Object.keys(defaultData);

  for (let i = defaultKeys.length; i--;) {
    const key = defaultKeys[i];

    if (key in data) {
      hasChanges = true;
      newDefaults = newDefaults || {};
      newDefaults[key] = data[key];
    }
  }

  return hasChanges ? Object.assign({}, defaultData, {}, newDefaults) : defaultData;
}
/**
 * Creates an optimized isEqual function based on a set of keys.
 *
 * @param  {Array.<String>} keys
 * @return {Function}       (objA, objB) => Boolean
 */

export function createOptimizedIsEqual(keys) {
  if (!Array.isArray(keys)) {
    throw new TypeError(`Expected array param in createOptimizedIsEqual, received '${typeof keys}'.`);
  }

  return function optimizedIsEqual(objA, objB) {
    return objA === objB || keys.every(k => objA[k] === objB[k]);
  };
}